import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../common/LanguageSwitcher/LanguageSwitcher';

const REGISTRATION_URL = `${window.location.origin}/new-token`;

// Simple QR code using a public API (no npm package needed)
function QRCodeImage({ value, size = 280 }) {
    const encoded = encodeURIComponent(value);
    const src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encoded}&margin=10&color=1e3a8a`;
    return <img src={src} alt='QR Code' width={size} height={size} style={{ borderRadius: 8 }} />;
}

function QRCodeStand() {
    const { t } = useTranslation();

    const steps = [
        { icon: <SmartphoneRoundedIcon sx={{ fontSize: 32, color: '#fff' }} />, text: t('kiosk.scanQR') },
        { icon: <PhotoCameraRoundedIcon sx={{ fontSize: 32, color: '#fff' }} />, text: t('kiosk.fillForm') },
        { icon: <ConfirmationNumberRoundedIcon sx={{ fontSize: 32, color: '#fff' }} />, text: t('kiosk.getTicket') },
    ];

    return (
        <PageRoot>
            <StandCard>
                {/* Logo + Language Switcher */}
                <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 4 }}>
                    <LogoCircle>
                        <Logo src='/logo5.png' alt='Dulaty University' />
                    </LogoCircle>
                    <LanguageSwitcher variant='light' />
                </Stack>

                {/* Header */}
                <Typography sx={{ fontWeight: 900, fontSize: { xs: 28, md: 40 }, color: '#1e3a8a', textAlign: 'center', lineHeight: 1.2, mb: 4 }}>
                    {t('kiosk.getNumber')}
                </Typography>

                {/* QR Code */}
                <Stack alignItems='center' sx={{ mb: 5 }}>
                    <Box sx={{ p: 3, border: '4px solid #1e3a8a', borderRadius: 3, display: 'inline-block', boxShadow: '0 8px 32px rgba(30,58,138,0.2)' }}>
                        <QRCodeImage value={REGISTRATION_URL} size={280} />
                    </Box>
                </Stack>

                {/* Steps */}
                <Stack spacing={3} sx={{ mb: 4 }}>
                    {steps.map((step, i) => (
                        <Stack key={i} direction='row' alignItems='center' spacing={2.5}>
                            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {step.icon}
                            </Box>
                            <Typography sx={{ fontSize: 20, color: '#374151' }}>
                                <strong style={{ color: '#1e3a8a' }}>{i + 1}.</strong> {step.text}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>

                {/* Footer */}
                <Box sx={{ bgcolor: '#eff6ff', borderRadius: 2, p: 2.5, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 16, color: '#6b7280' }}>
                        {t('kiosk.afterReg')}
                    </Typography>
                </Box>
            </StandCard>
        </PageRoot>
    );
}

const PageRoot = styled(Box)(() => ({
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32
}));

const StandCard = styled(Box)(() => ({
    background: '#fff', borderRadius: 32, padding: 48,
    maxWidth: 640, width: '100%',
    boxShadow: '0 32px 80px rgba(0,0,0,0.25)'
}));

const LogoCircle = styled(Box)(() => ({
    width: 96, height: 96, borderRadius: '50%',
    background: '#1e3a8a', display: 'flex',
    alignItems: 'center', justifyContent: 'center'
}));

const Logo = styled('img')(() => ({
    width: 60, height: 60, objectFit: 'contain',
    filter: 'brightness(0) invert(1)'
}));

export default QRCodeStand;
